import React, { useState, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import { GET_SERVICE_TYPES } from '../../graphql/queries';
import PropertySearch from './PropertySearch';
import JobFormFields from './JobFormFields';
import UserAssignment from './UserAssignment';
import FinancialFields from './FinancialFields';
import RecurringJobConfig from './RecurringJobConfig';
import useJobCreation from './hooks/useJobCreation';
import useRecurringJob from './hooks/useRecurringJob';
import { 
  validateJobForm, 
  validateFinancialFields, 
  hasValidationErrors,
  getFirstErrorMessage 
} from './utils/validationUtils';
import { 
  getCategoryFromServiceType,
  getServiceTypeById
} from './utils/jobUtils';
import { getCurrentDateString } from './utils/timeUtils';

/**
 * Refactored AddJobModal using modular components
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Close modal handler
 * @param {Function} props.onCreateJob - Job creation success handler
 * @param {string} props.initialPropertyId - Initial property ID to pre-populate
 * @param {Object} props.initialProperty - Initial property object to pre-populate
 * @param {string} props.initialNotes - Initial notes to pre-populate (for copy job functionality)
 * @param {string} props.initialScheduledDate - Initial scheduled date to pre-populate (for copy job functionality)
 * @param {string} props.initialPayout - Initial payout amount to pre-populate (for copy job functionality)
 * @param {string} props.initialBilledAmount - Initial billed amount to pre-populate (for copy job functionality)
 * @param {string} props.initialServiceTypeId - Initial service type ID to pre-populate (for order job creation)
 * @returns {JSX.Element} AddJobModal component
 */
const AddJobModal = ({ isOpen, onClose, onCreateJob, initialPropertyId, initialProperty, initialNotes, initialScheduledDate, initialPayout, initialBilledAmount, initialServiceTypeId }) => {
  // Form state
  const [formData, setFormData] = useState({
    propertyId: '',
    serviceTypeId: '',
    assignedToId: '',
    scheduledDate: getCurrentDateString(),
    startTime: '9:00 AM',
    category: 'CLEANING',
    notes: '',
    payout: '',
    billedAmount: '',
    // Recurring job fields
    frequency: 'WEEKLY',
    dayOfWeek: 3, // Wednesday in JavaScript's standard numbering (0=Sunday)
    dayOfMonth: 1, // Default for monthly jobs
    endDate: ''
  });

  const [selectedProperty, setSelectedProperty] = useState(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Fetch service types for category determination
  const { data: serviceTypesData } = useQuery(GET_SERVICE_TYPES, {
    skip: !isOpen
  });

  // Get the selected service type object
  const selectedServiceType = getServiceTypeById(formData.serviceTypeId, serviceTypesData?.serviceTypes);

  // Custom hooks for job creation
  const { 
    isCreating: isCreatingRegular, 
    successMessage: regularSuccessMessage, 
    errorMessage: regularErrorMessage,
    createRegularJob,
    clearMessages: clearRegularMessages
  } = useJobCreation(
    (jobData) => {
      onCreateJob?.(jobData);
      setTimeout(() => onClose(), 1500);
    },
    (error) => {
      // Error handling is managed by the hook
    }
  );

  const { 
    isCreating: isCreatingRecurring, 
    successMessage: recurringSuccessMessage, 
    errorMessage: recurringErrorMessage,
    createRecurring,
    clearMessages: clearRecurringMessages
  } = useRecurringJob(
    (jobData) => {
      setTimeout(() => onClose(), 1500);
    },
    (error) => {
      // Error handling is managed by the hook
    }
  );

  // Combined loading and message states
  const isCreating = isCreatingRegular || isCreatingRecurring;
  const successMessage = regularSuccessMessage || recurringSuccessMessage;
  const errorMessage = regularErrorMessage || recurringErrorMessage;

  // Update category when service type changes
  useEffect(() => {
    if (formData.serviceTypeId && serviceTypesData?.serviceTypes) {
      const newCategory = getCategoryFromServiceType(formData.serviceTypeId, serviceTypesData.serviceTypes);
      if (newCategory !== formData.category) {
        setFormData(prev => ({
          ...prev,
          category: newCategory
        }));
      }
    }
  }, [formData.serviceTypeId, serviceTypesData]);

  // Clear assigned user when property or service type changes (since eligible users may change)
  useEffect(() => {
    if (formData.assignedToId) {
      // Clear assignment when filtering criteria changes
      setFormData(prev => ({
        ...prev,
        assignedToId: ''
      }));
    }
  }, [selectedProperty?.area, formData.serviceTypeId]); // Only depend on the criteria that affect user eligibility, not the assignedToId itself

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      const initialCategory = initialServiceTypeId && serviceTypesData?.serviceTypes
        ? getCategoryFromServiceType(initialServiceTypeId, serviceTypesData.serviceTypes)
        : 'CLEANING';
      setFormData({
        propertyId: initialPropertyId || '',
        serviceTypeId: initialServiceTypeId || '',
        assignedToId: '',
        scheduledDate: initialScheduledDate || getCurrentDateString(),
        startTime: '9:00 AM',
        category: initialCategory,
        notes: initialNotes || '',
        payout: initialPayout || '',
        billedAmount: initialBilledAmount || '',
        frequency: 'WEEKLY',
        dayOfWeek: 3,
        dayOfMonth: 1,
        endDate: ''
      });
      setSelectedProperty(initialProperty || null);
      setIsRecurring(false);
      setValidationErrors({});
      clearRegularMessages();
      clearRecurringMessages();
    }
  }, [isOpen, initialPropertyId, initialProperty, initialNotes, initialScheduledDate, initialPayout, initialBilledAmount, initialServiceTypeId]);

  /**
   * Handle form data changes
   * @param {Object} newFormData - Updated form data
   */
  const handleFormDataChange = (newFormData) => {
    setFormData(newFormData);
    
    // Clear validation errors for changed fields
    if (hasValidationErrors(validationErrors)) {
      const updatedErrors = { ...validationErrors };
      Object.keys(newFormData).forEach(key => {
        if (newFormData[key] !== formData[key] && updatedErrors[key]) {
          delete updatedErrors[key];
        }
      });
      setValidationErrors(updatedErrors);
    }
  };

  /**
   * Handle property selection
   * @param {Object} property - Selected property
   */
  const handlePropertySelect = (property) => {
    setSelectedProperty(property);
    setFormData(prev => ({
      ...prev,
      propertyId: property?.id || ''
    }));
  };

  /**
   * Handle property ID change
   * @param {string} propertyId - New property ID
   */
  const handlePropertyIdChange = (propertyId) => {
    setFormData(prev => ({
      ...prev,
      propertyId
    }));
  };

  /**
   * Handle user assignment change
   * @param {string} assignedToId - New assigned user ID
   */
  const handleAssignedToIdChange = (assignedToId) => {
    setFormData(prev => ({
      ...prev,
      assignedToId
    }));
  };

  /**
   * Handle recurring job toggle
   * @param {boolean} recurring - Whether job is recurring
   */
  const handleRecurringChange = (recurring) => {
    setIsRecurring(recurring);
    
    // Clear recurring validation errors when toggling off
    if (!recurring && validationErrors) {
      const updatedErrors = { ...validationErrors };
      ['endDate', 'dayOfWeek', 'dayOfMonth'].forEach(field => {
        delete updatedErrors[field];
      });
      setValidationErrors(updatedErrors);
    }
  };

  /**
   * Validate the form
   * @returns {boolean} Whether form is valid
   */
  const validateForm = () => {
    // Basic validation
    const basicErrors = validateJobForm(formData, isRecurring);
    
    // Financial validation
    const financialErrors = validateFinancialFields(formData.payout, formData.billedAmount);
    
    const allErrors = { ...basicErrors, ...financialErrors };
    
    setValidationErrors(allErrors);
    return !hasValidationErrors(allErrors);
  };

  /**
   * Handle form submission
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    clearRegularMessages();
    clearRecurringMessages();
    
    // Validate form
    if (!validateForm()) {
      return;
    }

    // Verify we have a selected property
    if (!selectedProperty) {
      setValidationErrors({ propertyId: 'Selected property information is missing. Please select a property again.' });
      return;
    }

    try {
      if (isRecurring) {
        await createRecurring(formData, selectedProperty);
      } else {
        await createRegularJob(formData, selectedProperty);
      }
    } catch (error) {
      // Error handling is managed by the hooks
    }
  };

  // Don't render if modal is closed
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-full w-full p-4">
        <div className="bg-white rounded-lg shadow-lg w-full max-w-xl overflow-hidden my-auto">
          {/* Header */}
          <div className="px-6 py-4 bg-blue-500 text-white">
            <h2 className="text-xl font-semibold">Add New Job</h2>
          </div>
          
          <form onSubmit={handleSubmit} className="p-6">
            {/* Error Message */}
            {errorMessage && (
              <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
                {errorMessage}
              </div>
            )}
            
            {/* Success Message */}
            {successMessage && (
              <div className="mb-4 p-3 bg-green-100 text-green-700 rounded-md flex items-center">
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                {successMessage}
              </div>
            )}
            
            {/* Property Search */}
            <PropertySearch
              isOpen={isOpen}
              onPropertySelect={handlePropertySelect}
              selectedProperty={selectedProperty}
              propertyId={formData.propertyId}
              onPropertyIdChange={handlePropertyIdChange}
            />
            
            {/* Job Form Fields */}
            <JobFormFields
              formData={formData}
              onChange={handleFormDataChange}
              errors={validationErrors}
            />
            
            {/* User Assignment */}
            <UserAssignment
              assignedToId={formData.assignedToId}
              onAssignedToIdChange={handleAssignedToIdChange}
              category={formData.category}
              selectedProperty={selectedProperty}
              selectedServiceType={selectedServiceType}
              scheduledDate={formData.scheduledDate}
              errors={validationErrors}
            />
            
            {/* Financial Fields */}
            <FinancialFields
              formData={formData}
              onChange={handleFormDataChange}
              serviceTypeId={formData.serviceTypeId}
              serviceTypes={serviceTypesData?.serviceTypes}
              selectedProperty={selectedProperty}
              errors={validationErrors}
            />
            
            {/* Recurring Job Configuration */}
            <RecurringJobConfig
              formData={formData}
              onChange={handleFormDataChange}
              isRecurring={isRecurring}
              onRecurringChange={handleRecurringChange}
              errors={validationErrors}
            />
            
            {/* Action Buttons */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-gray-700 border rounded-md hover:bg-gray-50"
                disabled={isCreating}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-blue-300 flex items-center gap-2"
              >
                {isCreating && (
                  <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {isCreating ? 'Creating...' : `Create ${isRecurring ? 'Recurring ' : ''}Job`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddJobModal; 